<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert v-if="!isUserAdmin" type="error" outlined class="mt-8">
          You are not allowed to access SMT Admin Consent.
        </v-alert>

        <v-card v-else class="mt-8 mb-8 smt-card" :loading="loading">
          <template slot="progress">
            <v-progress-linear color="#006c27" indeterminate height="6"></v-progress-linear>
          </template>

          <v-card-title class="grey--text text--darken-2 smt-toolbar">
            <div>
              <div class="text-h6 font-weight-bold">SMT Admin Consent</div>
              <div class="text-caption grey--text text--darken-1 mt-1">
                Showing {{ filteredRows.length }} of {{ rows.length }} proxies | Export AppIDs: {{ filteredMappedClientIdsCount }}
              </div>
            </div>

            <v-spacer />

            <v-btn
              text
              color="#006c27"
              class="mr-2"
              :loading="loading && activeLoadAction === 'reload'"
              :disabled="loading"
              @click="loadData(false, 'reload')"
            >
              Reload
            </v-btn>
            <v-btn
              color="#006c27"
              dark
              class="mr-2"
              :loading="loading && activeLoadAction === 'force'"
              :disabled="loading"
              @click="loadData(true, 'force')"
            >
              Force Refresh
            </v-btn>
            <v-btn outlined color="#006c27" class="mr-2" @click="statsDialog = true">
              Stats
            </v-btn>
            <v-btn outlined color="#006c27" :disabled="filteredRows.length === 0 || loading" @click="downloadCsv">
              Download CSV
            </v-btn>
          </v-card-title>

          <v-card-text>
            <div class="text-caption grey--text mb-3">
              Cache refreshed: {{ formatDate(cache.refreshedAt) }} | cache hit: {{ cache.cacheHit ? "yes" : "no" }}
            </div>

            <v-row dense class="mb-2">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="searchQuery"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  label="Search proxy, base path, URI, clientId, servicePrincipalId, lookup error"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="activePreset"
                  :items="filterPresets"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  label="Filter preset"
                />
              </v-col>
            </v-row>

            <div class="mb-3">
              <v-chip small class="mr-2" color="#e8f5e9" text-color="#1b5e20">
                Unmapped in view: {{ filteredUnmappedRowsCount }}
              </v-chip>
              <v-chip small class="mr-2" color="#e3f2fd" text-color="#0d47a1">
                Fully mapped in view: {{ filteredFullyMappedRowsCount }}
              </v-chip>
            </div>

            <v-data-table
              :headers="headers"
              :items="filteredRows"
              :items-per-page="15"
              item-key="rowKey"
              :item-class="rowClass"
              class="elevation-1 smt-table"
            >
              <template v-slot:[`item.proxy`]='{ item }'>
                <div class="text-caption proxy-cell">
                  <div class="proxy-name">{{ item.proxyName }}</div>
                  <div class="proxy-path">{{ item.basePath || "-" }}</div>
                </div>
              </template>
              <template v-slot:[`item.dev`]='{ item }'>
                <div class="text-caption env-cell">
                  <div class="env-row">
                    <span class="env-key">URI</span>
                    <span class="env-value mono">{{ item.dev.appRegUri || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">ID</span>
                    <span class="env-value mono">{{ item.dev.clientId || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">SP</span>
                    <span class="env-value mono">{{ item.dev.servicePrincipalId || "-" }}</span>
                  </div>
                  <div v-if="item.dev.lookupError" class="env-row env-row--chip">
                    <span class="env-key">Issue</span>
                    <v-chip x-small color="#ffebee" text-color="#b71c1c" class="env-chip">
                      {{ item.dev.lookupError }}
                    </v-chip>
                  </div>
                </div>
              </template>
              <template v-slot:[`item.qas`]='{ item }'>
                <div class="text-caption env-cell">
                  <div class="env-row">
                    <span class="env-key">URI</span>
                    <span class="env-value mono">{{ item.qas.appRegUri || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">ID</span>
                    <span class="env-value mono">{{ item.qas.clientId || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">SP</span>
                    <span class="env-value mono">{{ item.qas.servicePrincipalId || "-" }}</span>
                  </div>
                  <div v-if="item.qas.lookupError" class="env-row env-row--chip">
                    <span class="env-key">Issue</span>
                    <v-chip x-small color="#ffebee" text-color="#b71c1c" class="env-chip">
                      {{ item.qas.lookupError }}
                    </v-chip>
                  </div>
                </div>
              </template>
              <template v-slot:[`item.prod`]='{ item }'>
                <div class="text-caption env-cell">
                  <div class="env-row">
                    <span class="env-key">URI</span>
                    <span class="env-value mono">{{ item.prod.appRegUri || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">ID</span>
                    <span class="env-value mono">{{ item.prod.clientId || "-" }}</span>
                  </div>
                  <div class="env-row">
                    <span class="env-key">SP</span>
                    <span class="env-value mono">{{ item.prod.servicePrincipalId || "-" }}</span>
                  </div>
                  <div v-if="item.prod.lookupError" class="env-row env-row--chip">
                    <span class="env-key">Issue</span>
                    <v-chip x-small color="#ffebee" text-color="#b71c1c" class="env-chip">
                      {{ item.prod.lookupError }}
                    </v-chip>
                  </div>
                </div>
              </template>
              <template slot="no-data">
                <div v-if="loading">Loading entries ...</div>
                <div v-else>No entries found.</div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="statsDialog" max-width="820">
      <v-card>
        <v-card-title class="headline">SMT Admin Consent Stats</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="stats-title">Proxy</div>
              <div class="stats-line">Proxies total: {{ stats.proxy.proxiesTotal }}</div>
              <div class="stats-line">Proxies with endpoint used: {{ stats.proxy.proxiesWithEndpointUsed }}</div>
              <div class="stats-line">Proxy URIs total: {{ stats.proxy.proxyUrisTotal }}</div>
              <div class="stats-line">Proxy URIs unique: {{ stats.proxy.proxyUrisUnique }}</div>
              <div class="stats-line">Mapped unique: {{ stats.proxy.proxyUrisMappedUnique }}</div>
              <div class="stats-line">Unmapped unique: {{ stats.proxy.proxyUrisUnmappedUnique }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="stats-title">Service Principal</div>
              <div class="stats-line">Pages fetched: {{ stats.servicePrincipal.pagesFetched }}</div>
              <div class="stats-line">SP fetched: {{ stats.servicePrincipal.servicePrincipalsFetched }}</div>
              <div class="stats-line">SP URIs total: {{ stats.servicePrincipal.servicePrincipalUrisTotal }}</div>
              <div class="stats-line">SP URIs unique: {{ stats.servicePrincipal.servicePrincipalUrisUnique }}</div>
              <div class="stats-line">SP URIs used unique: {{ stats.servicePrincipal.servicePrincipalUrisUsedUnique }}</div>
              <div class="stats-line">SP URIs unused unique: {{ stats.servicePrincipal.servicePrincipalUrisUnusedUnique }}</div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="stats-title">Mapping</div>
              <div class="stats-line">Coverage: {{ formatCoverage(stats.mapping.coveragePercent) }}%</div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="#006c27" @click="statsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import cass from "../api/cass";

const ENVIRONMENTS = ["dev", "qas", "prod"];

function emptyStats() {
  return {
    proxy: {
      proxiesTotal: 0,
      proxiesWithEndpointUsed: 0,
      proxyUrisTotal: 0,
      proxyUrisUnique: 0,
      proxyUrisMappedUnique: 0,
      proxyUrisUnmappedUnique: 0,
    },
    servicePrincipal: {
      pagesFetched: 0,
      servicePrincipalsFetched: 0,
      servicePrincipalUrisTotal: 0,
      servicePrincipalUrisUnique: 0,
      servicePrincipalUrisUsedUnique: 0,
      servicePrincipalUrisUnusedUnique: 0,
    },
    mapping: {
      coveragePercent: 0,
    },
  };
}

export default {
  name: "SmtAdminConsent",
  data() {
    return {
      loading: false,
      activeLoadAction: "idle",
      rows: [],
      searchQuery: "",
      activePreset: "all",
      statsDialog: false,
      cache: {
        refreshedAt: null,
        cacheHit: false,
      },
      stats: emptyStats(),
      snackbar: {
        visible: false,
        color: "#006c27",
        text: "",
      },
      filterPresets: [
        { text: "All proxies", value: "all" },
        { text: "Has valid mapped URI", value: "has_valid_mapped_uri" },
        { text: "Has unmapped entries", value: "has_unmapped" },
        { text: "Fully mapped", value: "fully_mapped" },
        { text: "Has missing URI", value: "missing_uri" },
        { text: "Prod issues", value: "prod_issues" },
      ],
      headers: [
        { text: "Proxy", value: "proxy", sortable: false },
        { text: "Development", value: "dev", sortable: false },
        { text: "Quality", value: "qas", sortable: false },
        { text: "Production", value: "prod", sortable: false },
      ],
    };
  },
  computed: {
    isUserAdmin() {
      return (
        this.$userData &&
        this.$userData.cass &&
        this.$userData.cass.isUserAdmin === true
      );
    },
    filteredRows() {
      const normalizedQuery = this.normalize(this.searchQuery);

      return this.rows.filter((row) => {
        if (!this.matchesPreset(row)) {
          return false;
        }
        if (!normalizedQuery) {
          return true;
        }
        return this.buildSearchText(row).includes(normalizedQuery);
      });
    },
    filteredMappedClientIdsCount() {
      return this.collectClientIds(this.filteredRows).length;
    },
    filteredUnmappedRowsCount() {
      return this.filteredRows.filter((row) => this.hasLookupError(row)).length;
    },
    filteredFullyMappedRowsCount() {
      return this.filteredRows.filter((row) => this.isFullyMapped(row)).length;
    },
  },
  created() {
    if (this.isUserAdmin) {
      this.loadData(false, "initial");
    }
  },
  methods: {
    showSnackbar(text, color = "#006c27") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const dateValue = new Date(value);
      if (Number.isNaN(dateValue.getTime())) {
        return value;
      }
      return dateValue.toLocaleString();
    },
    formatCoverage(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return "0.00";
      }
      return numberValue.toFixed(2);
    },
    normalize(value) {
      return value == null ? "" : String(value).trim().toLowerCase();
    },
    isMappedEnv(envData) {
      return Boolean(
        envData &&
        envData.appRegUri &&
        envData.clientId &&
        envData.servicePrincipalId &&
        !envData.lookupError
      );
    },
    hasValidMappedUri(row) {
      return ENVIRONMENTS.some((env) => this.isMappedEnv(row[env] || {}));
    },
    hasLookupError(row) {
      return ENVIRONMENTS.some((env) => row[env] && row[env].lookupError);
    },
    hasMissingUri(row) {
      return ENVIRONMENTS.some((env) => {
        const envData = row[env] || {};
        return envData.propertyKey && !envData.appRegUri;
      });
    },
    hasProdIssues(row) {
      const prodData = row.prod || {};
      return Boolean(prodData.lookupError || (prodData.propertyKey && !prodData.appRegUri));
    },
    isFullyMapped(row) {
      return ENVIRONMENTS.every((env) => this.isMappedEnv(row[env] || {}));
    },
    matchesPreset(row) {
      if (this.activePreset === "all") {
        return true;
      }
      if (this.activePreset === "has_unmapped") {
        return this.hasLookupError(row);
      }
      if (this.activePreset === "has_valid_mapped_uri") {
        return this.hasValidMappedUri(row);
      }
      if (this.activePreset === "fully_mapped") {
        return this.isFullyMapped(row);
      }
      if (this.activePreset === "missing_uri") {
        return this.hasMissingUri(row);
      }
      if (this.activePreset === "prod_issues") {
        return this.hasProdIssues(row);
      }
      return true;
    },
    buildSearchText(row) {
      const values = [row.proxyName, row.basePath];
      ENVIRONMENTS.forEach((env) => {
        const envData = row[env] || {};
        values.push(
          envData.propertyKey,
          envData.appRegUri,
          envData.clientId,
          envData.servicePrincipalId,
          envData.lookupError
        );
      });
      return this.normalize(values.join(" "));
    },
    collectClientIds(rows) {
      const uniqueIds = new Set();
      const appIds = [];

      rows.forEach((row) => {
        ENVIRONMENTS.forEach((env) => {
          const envData = row[env] || {};
          const clientId = this.normalize(envData.clientId);
          if (!this.isMappedEnv(envData)) {
            return;
          }
          if (!clientId || uniqueIds.has(clientId)) {
            return;
          }
          uniqueIds.add(clientId);
          appIds.push(clientId);
        });
      });

      return appIds;
    },
    rowClass(item) {
      return this.hasLookupError(item) ? "row-has-errors" : "row-mapped";
    },
    buildCsvTimestamp() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, "0");
      return [
        now.getFullYear(),
        pad(now.getMonth() + 1),
        pad(now.getDate()),
        "-",
        pad(now.getHours()),
        pad(now.getMinutes()),
        pad(now.getSeconds()),
      ].join("");
    },
    async loadData(forceRefresh, action = "reload") {
      if (!this.isUserAdmin) {
        return;
      }
      this.activeLoadAction = action;
      this.loading = true;
      try {
        const response = await cass.getSmtAdminConsent(forceRefresh);
        const items = response && response.data && Array.isArray(response.data.items)
          ? response.data.items
          : [];
        this.rows = items.map((item, index) => ({
          rowKey: `${item.proxyName || "proxy"}-${item.basePath || "base"}-${index}`,
          proxyName: item.proxyName || "",
          basePath: item.basePath || "",
          dev: item.appRegs && item.appRegs.dev ? item.appRegs.dev : {},
          qas: item.appRegs && item.appRegs.qas ? item.appRegs.qas : {},
          prod: item.appRegs && item.appRegs.prod ? item.appRegs.prod : {},
        }));
        this.cache = response && response.data && response.data.cache
          ? response.data.cache
          : { refreshedAt: null, cacheHit: false };
        this.stats = response && response.data && response.data.stats
          ? response.data.stats
          : emptyStats();
        if (forceRefresh) {
          this.showSnackbar("Forced refresh completed.");
        }
      } catch (error) {
        this.showSnackbar(cass.handleError(error).message, "#b71c1c");
      } finally {
        this.loading = false;
        this.activeLoadAction = "idle";
      }
    },
    downloadCsv() {
      const appIds = this.collectClientIds(this.filteredRows);
      if (appIds.length === 0) {
        this.showSnackbar("No AppIDs in current filter selection.", "#ef6c00");
        return;
      }

      const lines = ["AppIDs", ...appIds];

      const payload = `${lines.join("\n")}\n`;
      const blob = new Blob([payload], { type: "text/csv;charset=utf-8;" });
      const fileURL = window.URL.createObjectURL(blob);
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.setAttribute("download", `cass-app-ids-${this.buildCsvTimestamp()}.csv`);
      document.body.appendChild(fileLink);
      fileLink.click();
      fileLink.remove();
      window.URL.revokeObjectURL(fileURL);
      this.showSnackbar(`CSV download started (${appIds.length} AppIDs).`);
    },
  },
};
</script>

<style scoped>
.smt-card {
  border-radius: 12px;
}

.smt-toolbar {
  align-items: flex-start;
  gap: 8px;
}

.env-cell {
  min-width: 260px;
  line-height: 1.4;
  padding-top: 4px;
  padding-bottom: 4px;
}

.env-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  margin-bottom: 2px;
}

.env-key {
  font-family: "Roboto Condensed", "Trebuchet MS", sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #33691e;
  text-transform: uppercase;
}

.env-value {
  min-width: 0;
  word-break: break-word;
}

.env-row--chip {
  margin-top: 4px;
  margin-bottom: 4px;
}

.env-chip {
  justify-self: start;
}

.proxy-cell {
  line-height: 1.4;
}

.proxy-name {
  font-weight: 700;
  font-size: 13px;
}

.proxy-path {
  color: #546e7a;
}

.mono {
  font-family: "Roboto Mono", "Courier New", monospace;
}

.stats-title {
  font-weight: 700;
  margin-bottom: 6px;
  color: #1b5e20;
}

.stats-line {
  margin-bottom: 3px;
}

.smt-table :deep(.row-has-errors) {
  background-color: #fff8f8;
}

.smt-table :deep(.row-mapped) {
  background-color: #fbfdfb;
}
</style>
